/**
 * Layout adaptativo da grade de participantes (1:1 → muitas pessoas).
 * A partir de 4 remotes a grade é igualitária (sem faixa larga de 100px).
 * Aviso de capacidade a partir de 10.
 */

import {
  CAMERA_TILE_ASPECT,
  voFloorCrowdGap,
  voFloorSeatFrameSx,
} from "./videoMediaPresence";

export { CAMERA_TILE_ASPECT };

export const SPOTLIGHT_FROM_REMOTES = 4;
export const CROWD_WARNING_FROM_REMOTES = 10;
export const MAX_MAIN_GRID_TILES = 9;
export const TRACK_PRIORITY_FROM_REMOTES = 5;

const CAMERA_MEDIA_SX = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "center center",
  backgroundColor: "#000",
  display: "block",
};

function withCameraTiles(sx) {
  return {
    ...sx,
    justifyContent: sx.justifyContent ?? "center",
    alignContent: sx.alignContent ?? "center",
    "& > [data-sid]": {
      ...(sx["& > [data-sid]"] || {}),
      position: "relative",
      aspectRatio: CAMERA_TILE_ASPECT,
      width: "100%",
      height: "auto",
      maxWidth: "100%",
      maxHeight: "100%",
      justifySelf: "center",
      alignSelf: "center",
      overflow: "hidden",
      backgroundColor: "#000",
      "& > video, & > canvas": CAMERA_MEDIA_SX,
    },
  };
}

/**
 * @param {number} remoteCount
 * @param {{ presenting?: boolean, voFloorScene?: boolean }} [opts]
 * @returns {"solo"|"pair"|"grid2"|"grid3"|"spotlight"|"filmstrip"|"filmstripCompact"|"voFloor"}
 */
export function layoutMode(remoteCount, { presenting = false, voFloorScene = false } = {}) {
  const n = Math.max(0, Number(remoteCount) || 0);
  if (presenting) {
    if (n <= 0) return "solo";
    if (n >= 3) return "filmstrip";
    return "filmstripCompact";
  }
  if (voFloorScene) return "voFloor";
  if (n <= 0) return "solo";
  if (n === 1) return "pair";
  if (n === 2) return "grid2";
  if (n === 3) return "grid3";
  return "spotlight";
}

export function overflowParticipantCount(remoteCount, max = MAX_MAIN_GRID_TILES) {
  const n = Math.max(0, Number(remoteCount) || 0);
  const limit = Math.max(1, Number(max) || MAX_MAIN_GRID_TILES);
  return Math.max(0, n - limit);
}

export function shouldWarnCrowd(remoteCount) {
  return Math.max(0, Number(remoteCount) || 0) >= CROWD_WARNING_FROM_REMOTES;
}

export function shouldUseTrackPriority(remoteCount) {
  return Math.max(0, Number(remoteCount) || 0) >= TRACK_PRIORITY_FROM_REMOTES;
}

/**
 * Grade que cabe no palco e mantém o tile perto de uma proporção assistível.
 * 4 pessoas: 2×2. 5–6: 3 colunas. 7–9: 3×3. Sem `auto-fill` (linhas fantasmas
 * deixavam o vídeo numa faixa de ~100px no desktop largo).
 * @param {number} remoteCount
 * @returns {{ cols: number, rows: number, count: number }}
 */
export function spotlightGridShape(remoteCount) {
  const count = Math.min(
    Math.max(SPOTLIGHT_FROM_REMOTES, Number(remoteCount) || SPOTLIGHT_FROM_REMOTES),
    MAX_MAIN_GRID_TILES
  );
  const cols = count >= 5 ? 3 : 2;
  const rows = Math.ceil(count / cols);
  return { cols, rows, count };
}

function frRows(count) {
  const n = Math.max(1, Number(count) || 1);
  return `repeat(${n}, minmax(0, 1fr))`;
}

/**
 * Estilos MUI `sx` para o container da grade remota.
 */
export function voFloorRugSx(crowd = "roomy") {
  return {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: { xs: 4, md: "3%" },
    zIndex: 3,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-end",
    alignContent: "flex-end",
    gap: voFloorCrowdGap(crowd),
    px: { xs: 1.25, md: 6 },
    pr: { xs: 1.25, md: 22 },
    maxHeight: { xs: "54%", md: "62%" },
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    pointerEvents: "auto",
    "& > [data-sid][data-hidden-overflow='1']": {
      display: "none !important",
    },
  };
}

export function remoteGridSx(mode, { remoteCount = 0, crowd = "roomy" } = {}) {
  const n = Math.max(0, Number(remoteCount) || 0);
  const base = {
    display: "grid",
    gap: 1.5,
    minWidth: 0,
    minHeight: 0,
    "& > [data-sid]": {
      transition: "box-shadow 0.2s ease, outline 0.2s ease",
    },
    "& > [data-sid][data-speaking='1']": {
      outline: "2px solid #8ab4f8",
      outlineOffset: "-2px",
      boxShadow: "0 0 0 1px rgba(138,180,248,0.35)",
    },
    "& > [data-sid][data-pinned='1']": {
      outline: "2px solid #fdd663",
      outlineOffset: "-2px",
    },
    "& > [data-sid][data-hidden-overflow='1']": {
      display: "none !important",
    },
  };

  if (mode === "solo") {
    return { ...base, display: "none" };
  }

  if (mode === "voFloor") {
    const idle = voFloorSeatFrameSx({ camOn: false, crowd });
    const live = voFloorSeatFrameSx({ camOn: true, crowd });
    return {
      display: "contents",
      "& > [data-sid]": {
        ...idle,
        aspectRatio: "unset",
        maxWidth: { md: idle.width.md },
        maxHeight: "none",
        borderRadius: "28px",
        background: "transparent",
        boxShadow: "none",
        transition: "box-shadow 0.2s ease, outline 0.2s ease",
        animation: "voSeatIn 0.55s ease both",
        "@keyframes voSeatIn": {
          from: { opacity: 0, transform: "translateY(14px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      },
      "& > [data-sid][data-speaking='1']": {
        outline: "2px solid #8ab4f8",
        outlineOffset: "-2px",
        boxShadow: "0 0 0 1px rgba(138,180,248,0.35)",
      },
      "& > [data-sid][data-pinned='1']": {
        outline: "2px solid #fdd663",
        outlineOffset: "-2px",
      },
      "& > [data-sid][data-vo-live-cam='1']": {
        width: live.width,
        height: live.height,
        maxWidth: { md: live.width.md },
      },
      "& > [data-sid][data-hidden-overflow='1']": {
        display: "none !important",
      },
    };
  }

  if (mode === "filmstrip" || mode === "filmstripCompact") {
    const wide = mode === "filmstrip";
    return withCameraTiles({
      ...base,
      gridTemplateColumns: {
        xs: "repeat(auto-fill, minmax(120px, 1fr))",
        md: "1fr",
      },
      gridAutoRows: { xs: "minmax(100px, 120px)", md: "minmax(120px, 140px)" },
      alignContent: "start",
      overflowY: { xs: "visible", md: "auto" },
      overflowX: "hidden",
      flex: { xs: "0 0 auto", md: wide ? "0 0 260px" : "0 0 220px" },
      width: { xs: "100%", md: wide ? 260 : 220 },
      maxHeight: { md: "100%" },
      height: { xs: "auto", md: "100%" },
      minHeight: { xs: 100, md: "100%" },
    });
  }

  if (mode === "pair") {
    return withCameraTiles({
      ...base,
      gridTemplateColumns: "1fr",
      gridAutoRows: "minmax(0, 1fr)",
      flex: 1,
      width: "100%",
      height: "100%",
      minHeight: 0,
    });
  }

  if (mode === "grid2") {
    return withCameraTiles({
      ...base,
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gridAutoRows: "minmax(140px, 1fr)",
      flex: 1,
      width: "100%",
      height: "100%",
      minHeight: 0,
    });
  }

  if (mode === "grid3") {
    return withCameraTiles({
      ...base,
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
      gridTemplateRows: { xs: "repeat(3, minmax(0, 1fr))", sm: "minmax(0, 1fr)" },
      gridAutoRows: "minmax(0, 1fr)",
      flex: 1,
      width: "100%",
      height: "100%",
      minHeight: 0,
    });
  }

  // 4–9: grade igualitária. Tiles 16:9 centrados na célula — sem esticar o
  // quadro (cover + célula larga comia testa/laterais conforme o nº de pessoas).
  const shape = spotlightGridShape(n);
  return withCameraTiles({
    ...base,
    gridTemplateColumns: {
      xs: "1fr",
      sm: shape.cols === 3 ? "1fr 1fr 1fr" : "1fr 1fr",
    },
    gridTemplateRows: {
      xs: frRows(shape.count),
      sm: frRows(shape.rows),
    },
    gridAutoRows: "minmax(0, 1fr)",
    flex: 1,
    width: "100%",
    height: "100%",
    minHeight: 0,
  });
}

/**
 * Sid a destacar no spotlight (pin > dominant > primeiro da lista estável).
 * @param {string[]} orderedSids
 * @param {{ pinnedSid?: string|null, dominantSid?: string|null }} focus
 */
export function resolveSpotlightSid(orderedSids, { pinnedSid = null, dominantSid = null } = {}) {
  const list = Array.isArray(orderedSids) ? orderedSids.filter(Boolean) : [];
  if (!list.length) return null;
  if (pinnedSid && list.includes(pinnedSid)) return pinnedSid;
  if (dominantSid && list.includes(dominantSid)) return dominantSid;
  return list[0];
}

/**
 * Quais remotes ficam visíveis no grid (foco nunca some no overflow).
 * @param {string[]} orderedSids
 * @param {{ pinnedSid?: string|null, dominantSid?: string|null, max?: number }} opts
 */
export function selectVisibleRemoteSids(
  orderedSids,
  { pinnedSid = null, dominantSid = null, max = MAX_MAIN_GRID_TILES } = {}
) {
  const list = Array.isArray(orderedSids) ? orderedSids.filter(Boolean) : [];
  const limit = Math.max(1, Number(max) || MAX_MAIN_GRID_TILES);
  if (list.length <= limit) return list;

  const spotlightSid = resolveSpotlightSid(list, { pinnedSid, dominantSid });
  const priority = [spotlightSid, pinnedSid, dominantSid].filter(
    (sid, i, arr) => sid && list.includes(sid) && arr.indexOf(sid) === i
  );
  const visible = [...priority];
  for (const sid of list) {
    if (visible.length >= limit) break;
    if (!visible.includes(sid)) visible.push(sid);
  }
  return visible;
}
